import React, { useCallback } from 'react';
import { css } from '@emotion/core';
import { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import Select, { AsyncSelect, ValueType } from '@atlaskit/select';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import ToolTip from '@atlaskit/tooltip';

import { EditContext, useAutoCompleteController } from '@/controllers';
import {
  borderColor,
  cursorPointer,
  flexStart,
  lessThanUnitStyles,
  spaceGapMinimum,
} from '@/styles';
import { ConditionalErrorMessage } from '@/components/composed/forms/form-errors';
import { useActions } from '@/components/primitive/actions';
import {
  sendAnalyticsEvent,
  EventMetricsTypes,
  EventMetricsKeys,
} from '@/common/ufo/analytics-events';

import OwnedBySlo, { getState } from './owned-by-slo';
import * as FieldValidation from './field-validation';

const populationStyles = css`
  padding-top: 0.5rem;
  padding-left: 1rem;
  border-left: 2px solid ${borderColor};
`;

const performanceTargetStyles = [
  lessThanUnitStyles('ms'),
  css`
    min-width: 140px;
  `,
];

type UIMetricsSliPickerProps = Partial<API.UIMetrics.Config>;

const UIMetricsSliPicker: React.FC<UIMetricsSliPickerProps> = ({ experience, population }) => {
  /*
  Provide controlled state for the Experience field.
  The available Populations are determined by the selected Experience.
  */
  const [selectedExperience, setSelectedExperience] = React.useState<
    API.UIMetrics.ExperienceFieldType
  >();

  const [
    selectedPopulation,
    setSelectedPopulation,
  ] = React.useState<API.UIMetrics.PopulationFieldType | null>();

  const [performanceTarget, setPerformanceTarget] = React.useState<HTMLTextAreaElement['value']>(
    ''
  ); // NOTE: So internally in the component this value is a number, but the input element is a string.

  const loadExperiences = useAutoCompleteController('perfportal');

  /*
  Pre-fill the form only when an external Experience / Population prop is supplied.
  */
  React.useEffect(() => {
    setSelectedExperience(experience);
    setSelectedPopulation(population);
    setPerformanceTarget(population?.performanceTarget ?? '');
  }, [experience, population]);

  /*
  Safeguard users from unsuspectingly setting Performance Targets for
  another teams' SLOs by checking if the Populations' associated SLO (if one exists)
  is the same as the current SLO being edited.
  */
  const { publicId: currentSloPublicId } = React.useContext(EditContext);
  const isPopulationOwnedByOther: boolean = !!(
    selectedPopulation &&
    getState({ slo: selectedPopulation?.slo, currentSloPublicId }) === 'OwnedByOther'
  );
  const shouldDisablePerformanceTargetField: boolean =
    !selectedPopulation || isPopulationOwnedByOther;

  const { onCopy } = useActions(selectedExperience?.value ?? '', undefined, 'slo');

  const copyExperienceKey = useCallback(() => {
    onCopy();
    sendAnalyticsEvent(EventMetricsTypes.UI, EventMetricsKeys.CreateClickedCopyExperienceKeyButton);
  }, [onCopy]);

  // This is required as a const to avoid eslint failing due to long lines...it also wont let us string split.
  const performanceTargetRequiredError =
    'Performance SLOs using Glance data need to specify a Performance Target.';

  return (
    <>
      <Field<ValueType<API.UIMetrics.ExperienceFieldType>>
        name="experienceKey"
        label={
          <div css={[flexStart, spaceGapMinimum]}>
            <span>Glance - Experience Event Key</span>
            {selectedExperience && (
              <span css={[cursorPointer]} onClick={copyExperienceKey}>
                <ToolTip position="top" content="Copy">
                  <CopyIcon size="small" label="Copy" />
                </ToolTip>
              </span>
            )}
          </div>
        }
        defaultValue={selectedExperience}
      >
        {({ fieldProps: { id, onChange, ...rest }, error }) => (
          <>
            <AsyncSelect<API.UIMetrics.ExperienceFieldType>
              placeholder="e.g. 'jira.fe.page-load.issue-view'"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
              inputId={id}
              defaultOptions
              loadOptions={loadExperiences}
              value={selectedExperience}
              onChange={args => {
                onChange(args);
                setSelectedExperience(args as API.UIMetrics.ExperienceFieldType);
                setSelectedPopulation(null);
                setPerformanceTarget(' ');
              }}
              validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
            />
            <ConditionalErrorMessage
              error={error}
              requiredErrorString={
                'Performance SLOs using Glance data need to specify' +
                ' an Experience Event Key and Population Split in order for a Performance Target' +
                ' threshold to be set.'
              }
            />
          </>
        )}
      </Field>
      <div css={populationStyles}>
        {selectedExperience && selectedPopulation && <OwnedBySlo slo={selectedPopulation?.slo} />}
        <Field<ValueType<API.UIMetrics.PopulationFieldType>>
          name="population"
          label={`Glance - Population Split - {${selectedPopulation?.populationKeys ?? 'n/a'}}`}
          isDisabled={!selectedExperience}
          defaultValue={selectedPopulation}
          validate={selectedExperience ? FieldValidation.validatePopulation : () => {}}
        >
          {({ fieldProps: { id, onChange, ...rest }, error }) => (
            <>
              <Select<API.UIMetrics.PopulationFieldType>
                placeholder="e.g. 'tti', 'initial', 'enterprise'"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                inputId={id}
                options={selectedExperience && selectedExperience.populations}
                value={selectedPopulation}
                onChange={(args: API.UIMetrics.PopulationFieldType) => {
                  if (!args) return;
                  onChange(args);
                  setSelectedPopulation(args);
                  setPerformanceTarget(args.performanceTarget ?? '');
                }}
                validationState={error ? 'error' : 'default'} // https://product-fabric.atlassian.net/browse/DSP-821
              />
              <ConditionalErrorMessage
                error={error}
                requiredErrorString={
                  'Performance SLOs using Glance data need to specify a Population' +
                  ' Split in order for a Performance Target threshold to be set.'
                }
              />
            </>
          )}
        </Field>
        <Field<string>
          name="performanceTarget"
          label="Performance Target"
          isDisabled={shouldDisablePerformanceTargetField}
          defaultValue={performanceTarget}
          validate={
            selectedExperience && selectedPopulation && !isPopulationOwnedByOther
              ? FieldValidation.validatePerformanceTarget
              : () => {}
          }
        >
          {({ fieldProps: { id, onChange, ...rest }, error }) => (
            <>
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                id={id}
                type="number"
                placeholder="e.g. 3000"
                min={0}
                css={performanceTargetStyles}
                value={performanceTarget}
                onChange={event => {
                  onChange(event.currentTarget.value);
                  setPerformanceTarget(event.currentTarget.value);
                }}
              />
              <ConditionalErrorMessage
                error={error}
                requiredErrorString={performanceTargetRequiredError}
              />
            </>
          )}
        </Field>
      </div>
    </>
  );
};

export default UIMetricsSliPicker;
